import type { Hotel } from '@/config/objects/hotels';
import { Switch, toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface OffersReminderToggleProps {
  hotelId: string;
}

/**
 * Hotel-level opt-in for the guest "check your offers" reminder shown when a
 * tour starts (DT-220). Off by default — only when a hotel turns this on does
 * the mobile app show its guests the one-time reminder.
 *
 * Reads and writes the hotel's `offersReminderEnabled` flag, sharing the
 * `['Hotel', id]` query cache with the hotel detail shell so the value stays in
 * sync (no extra fetch) and a save refetches both.
 */
export function OffersReminderToggle({ hotelId }: OffersReminderToggleProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { data } = useQuery<{ data: Hotel }>({
    queryKey: ['Hotel', hotelId],
    queryFn: () => apiClient.get<{ data: Hotel }>(`/hotels/${hotelId}`),
    enabled: !!hotelId,
  });
  const enabled = data?.data?.offersReminderEnabled ?? false;

  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      apiClient.patch(`/hotels/${hotelId}`, { offersReminderEnabled: next }),
    onSuccess: (_result, next) => {
      toast.success(
        next ? 'Guests will be reminded to check your offers' : 'Offers reminder turned off',
      );
      void queryClient.invalidateQueries({ queryKey: ['Hotel', hotelId] });
    },
    onError: () => toast.error('Could not update the offers reminder setting'),
  });

  // Show the in-flight value while saving so the switch feels instant; on error
  // it falls back to the (unchanged) server value, which reverts the switch.
  const checked = mutation.isPending ? (mutation.variables ?? enabled) : enabled;

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-4 px-6 py-4">
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-zinc-900">Remind guests to check offers</h3>
          <p className="text-sm text-muted-foreground">
            When on, guests see a one-time reminder to view your offers as they start a tour. Off by
            default.
          </p>
        </div>
        <Switch
          checked={checked}
          disabled={mutation.isPending}
          onCheckedChange={next => mutation.mutate(next)}
          aria-label="Remind guests to check offers when they start a tour"
        />
      </div>
    </div>
  );
}
